import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { usePdfiumEngine } from '@embedpdf/engines/react';
import pdfiumWasm from '@embedpdf/pdfium/pdfium.wasm?url';
import { PdfEngineContext } from './PdfEngineContext';
import type { PdfEngineValue } from './PdfEngineContext';

/**
 * Serve pdfium from our own origin. Left unset, `usePdfiumEngine` falls back to
 * a jsdelivr CDN URL — a 4.3MB third-party fetch on the critical path of a PACS.
 *
 * Must be absolute: the engine builds its worker from a `blob:` URL and posts
 * this string into it, where a root-relative path would not resolve. Must also
 * stay at module scope — `usePdfiumEngine` keys its effect on this value, so a
 * per-render identity would destroy and rebuild the engine every render.
 */
const PDFIUM_WASM_URL =
  typeof window === 'undefined'
    ? pdfiumWasm
    : new URL(pdfiumWasm, window.location.origin).href;

type EngineState = Pick<PdfEngineValue, 'engine' | 'isLoading' | 'error'>;

const IDLE: EngineState = { engine: null, isLoading: false, error: null };

/**
 * Creates the engine once and keeps it alive for the lifetime of the app.
 *
 * `usePdfiumEngine` destroys its engine on unmount, so calling it inside the
 * preview pane meant every visit respawned the worker and recompiled 4.3MB of
 * wasm. Hoisting it here pays that once; `warmUp()` lets callers start the
 * compile early (e.g. when the reader opens) so it overlaps with the PDF fetch
 * instead of landing on the critical path.
 */
export function PdfEngineProvider({ children }: { children: ReactNode }) {
  const [warm, setWarm] = useState(false);
  const [state, setState] = useState<EngineState>(IDLE);

  const warmUp = useCallback(() => setWarm(true), []);

  const value = useMemo<PdfEngineValue>(
    () => ({ ...state, warmUp }),
    [state, warmUp]
  );

  return (
    <PdfEngineContext.Provider value={value}>
      {/* Renders no DOM, and mounts only once warmed — so pages that never show
          a PDF pay for neither the worker chunk nor the wasm. It is a sibling of
          `children` rather than a wrapper: swapping a wrapper component would
          remount everything below it, which here is the entire router. */}
      {warm && <PdfEngineBridge onState={setState} />}
      {children}
    </PdfEngineContext.Provider>
  );
}

/** Owns the engine and mirrors its state onto the context. */
function PdfEngineBridge({
  onState,
}: {
  onState: (state: EngineState) => void;
}) {
  const { engine, isLoading, error } = usePdfiumEngine({
    wasmUrl: PDFIUM_WASM_URL,
  });

  useEffect(() => {
    onState({ engine, isLoading, error });
  }, [engine, isLoading, error, onState]);

  return null;
}
